import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import googleIcon from "../../images/googleIcon.svg";
import axiosInstance from "../../utils/Requests/axios";
import LanguageContext from "../../utils/Contexts/LanguageContext";

function SignIn({ formData, setFormData }) {
  const navigate = useNavigate();
  const { t } = useContext(LanguageContext);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loginData, setLoginData] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  const validate = () => {
    const newErrors = {};
    // Check for email
    if (!/\S+@\S+\.\S+/.test(loginData.username)) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (loginData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long.";
    }
    return newErrors;
  };

  useEffect(() => {
    // Clear errors when the user starts typing
    const validationErrors = validate();
    setErrors(validationErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginData]);

  const handleSignIn = (e) => {
    e.preventDefault();
    setSubmitted(true);

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    axiosInstance
      .post("/auth/login", loginData)
      .then(() => {
        window.location.reload();
      })
      .catch((error) => {
        if (error?.status === 404) {
          setErrors({
            email: "An account with this email does not exists."
          });
        } else if (error?.status === 401 && error?.data?.message === 'password_not_valid') {
          setErrors({
            password: "Your password is invalid. Please try again."
          });
        } else if (error?.status === 401 && error?.data?.message === 'email_not_verified') {
          const dupObj = { ...formData };
          dupObj.username = loginData.username;
          setFormData(dupObj);
          navigate("/verify-email");
        } else if (error?.data?.message === 'account_locked') {
          setErrors({
            email: "This account has been locked."
          });
        }
      });
  };

  return (
    <div
      className="w-100 content-space-t-4 content-space-t-lg-2 content-space-b-1"
      style={{ maxWidth: "25rem" }}
    >
      <form
        className="js-validate needs-validation"
        noValidate=""
        onSubmit={handleSignIn}
      >
        <div className="text-center">
          <div className="mb-5">
            <h1 className="display-5">{t("Sign in")}</h1>
            <p>
              {t("Don't have an account yet?")}{" "}
              {/* eslint-disable-next-line */}
              <a
                className="link"
                onClick={() => navigate("/sign-up")}
                style={{ cursor: "pointer" }}
              >
                {t("Sign up here")}
              </a>
            </p>
          </div>
          <div className="d-grid mb-4">
            <a
              className="btn btn-white btn-lg"
              href={`${process.env.REACT_APP_API_BASE_URL}/auth/login/google`}
            >
              <span className="d-flex justify-content-center align-items-center">
                <img
                  className="avatar avatar-xss me-2"
                  src={googleIcon}
                  alt="Description"
                />
                {t("Sign in with Google")}
              </span>
            </a>
          </div>
          <span className="divider-center text-muted mb-4">{t("OR")}</span>
        </div>
        <div className="mb-4">
          <label className="form-label" htmlFor="signinSrEmail">
            {t("Your email")}
          </label>
          <input
            type="email"
            className={`form-control form-control-lg ${
              submitted && errors.email
                ? "is-invalid"
                : submitted && loginData.username
                ? "is-valid"
                : ""
            }`}
            name="email"
            id="signinSrEmail"
            tabIndex="1"
            placeholder="email@address.com"
            aria-label="email@address.com"
            required=""
            value={loginData.username}
            onChange={(e) => {
              const dupObj = { ...loginData };
              dupObj.username = e.target.value;
              setLoginData(dupObj);
            }}
          />
          {submitted && errors.email && (
            <span className="invalid-feedback">{t(errors.email)}</span>
          )}
        </div>
        <div className="mb-4">
          <label
            className="form-label w-100"
            htmlFor="signupSrPassword"
            tabIndex="0"
          >
            <span className="d-flex justify-content-between align-items-center">
              <span>{t("Password")}</span>
              {/* eslint-disable-next-line */}
              <a
                className="form-label-link mb-0"
                onClick={() => navigate("/forgot-password")}
                style={{ cursor: "pointer" }}
              >
                {t("Forgot Password?")}
              </a>
            </span>
          </label>
          <div
            className={`input-group input-group-merge ${
              submitted && errors.password
                ? "is-invalid"
                : submitted && loginData.password
                ? "is-valid "
                : ""
            }`}
            data-hs-validation-validate-classname=""
          >
            <input
              type={showPassword ? "text" : "password"}
              className="js-toggle-password form-control form-control-lg"
              name="password"
              id="signupSrPassword"
              placeholder="8+ characters required"
              aria-label="8+ characters required"
              required=""
              minLength="8"
              data-hs-toggle-password-options='{
                "target": "#changePassTarget",
                "defaultClass": "bi-eye-slash",
                "showClass": "bi-eye",
                "classChangeTarget": "#changePassIcon"
              }'
              value={loginData.password}
              onChange={(e) => {
                const dupObj = { ...loginData };
                dupObj.password = e.target.value;
                setLoginData(dupObj);
              }}
            />
            <div
              className="js-toggle-password-target-1 input-group-append input-group-text"
              onClick={() => setShowPassword(!showPassword)} // Toggle password visibility
              style={{ cursor: "pointer" }}
            >
              <i
                className={`js-toggle-password-show-icon-1 bi ${
                  showPassword ? "bi-eye" : "bi-eye-slash"
                }`}
              ></i>
            </div>
          </div>

          {submitted && errors.password && (
            <span className="invalid-feedback">
              {t(errors.password)}
            </span>
          )}
        </div>
        <div className="form-check mb-4">
          <input
            className="form-check-input"
            type="checkbox"
            value=""
            id="termsCheckbox"
          />
          <label className="form-check-label" htmlFor="termsCheckbox">
            {t("Remember me")}
          </label>
        </div>
        <div className="d-grid">
          <button type="submit" className="btn btn-primary btn-lg">
            {t("Sign in")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default SignIn;
